import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Check, 
  ArrowRight
} from 'lucide-react';
import { transactionApi } from '../../repositories/transaction';

const PricingPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await transactionApi.getAllStripeProducts();
        
        if (response.data && response.data.success) {
          setProducts(response.data.data);
        } else {
          setError('Failed to fetch products');
        }
      } catch (err:any) {
        setError('Error fetching products: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-12 h-12 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl p-4 mx-auto my-12 text-red-700 rounded-md bg-red-50">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="py-12 bg-gradient-to-r from-indigo-600 to-purple-600">
        <div className="px-4 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl md:text-6xl">
            Choose Your Pitch Credit Package
          </h1>
          <p className="max-w-md mx-auto mt-3 text-base text-indigo-100 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            One-time purchase. No subscriptions. Select the perfect pack for your business needs.
          </p>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-12 bg-white">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid max-w-6xl grid-cols-1 gap-8 px-4 mx-auto md:grid-cols-3">
            {products.map((product:any) => (
              <div 
                key={product.id} 
                className="relative flex flex-col h-full overflow-hidden transition-shadow duration-300 bg-white border border-gray-200 shadow-lg rounded-xl hover:shadow-xl"
              >
                {/* Card Header */}
                <div className="p-6 text-white bg-gradient-to-r from-indigo-500 to-purple-600">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold">{product.name}</h3>
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold">{product.metadata.pitchLimit}</span>
                    <span className="ml-2 text-sm opacity-80">AI-powered pitches</span>
                  </div>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">
                      ${(product.prices[0].unit_amount / 100).toFixed(2)}
                    </span>
                    <span className="ml-1 text-sm opacity-80">one-time</span>
                  </div>
                </div>
                
                {/* Card Body */}
                <div className="flex-grow p-6">
                  <p className="mb-6 text-sm leading-relaxed text-gray-700">
                    {product.description}
                  </p>
                  
                  <ul className="mb-6 space-y-3">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">Credits valid for 6 months</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">
                        {product.name === 'Starter Pack' ? 'Basic support' : 
                         product.name === 'Growth Pack' ? 'Priority email support' : 
                         'Premium priority support'}
                      </span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">
                        {product.name === 'Starter Pack' ? 'Standard features' : 
                         product.name === 'Growth Pack' ? 'Team collaboration' : 
                         'Analytics dashboard + API access'}
                      </span>
                    </li>
                    {product.name === 'Professional Pack' && (
                      <li className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600">
                          Dedicated account manager
                        </span>
                      </li>
                    )}
                  </ul>
                </div>
                
                {/* Card Footer */}
                <div className="p-6 border-t border-gray-100">
                  <a 
                    href={product.metadata.payment_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full px-4 py-3 font-medium text-white transition-colors bg-indigo-600 rounded-lg hover:bg-indigo-700"
                  >
                    Buy Now <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </div>
                
                {product.name === 'Growth Pack' && (
                  <div className="absolute top-0 right-0 px-3 py-1 text-xs font-semibold text-white transform translate-x-2 -translate-y-1 bg-gradient-to-r from-orange-500 to-pink-500 rounded-bl-xl rounded-tr-xl">
                    MOST POPULAR
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;